<template>
  <div>
    <title-bar :title-stack="['SECAPI', 'Acessos', 'Veículos']" />
    <CardComponent>
      <card-header
        :title="'Listar Acessos de Veículos'"
        :route="'/acessos/cadastrar/veiculo'"
      />
      <ModalDelete
        :isActive="modalDelectActive"
        :item="acessoSelecionado"
        @confirm="(id) => deleteAcesso(id)"
        @cancel="closeModal"
      />
      <ModalRecusarAcesso
        :isActive="modalRecusarActive"
        :item="acessoSelecionado"
        @confirm="(payload) => recusarAcesso(payload)"
        @cancel="closeModal"
        />
      <FilterAcessoVeiculo @filtrar="handleFilter" />
      <b-table
        :data="acessos"
        striped
        hoverable
        narrowed
        class="is-fullwidth tabela"
      >
        <b-table-column field="nome" label="Placa">
          <template slot-scope="props">
            {{ props.row.veiculo?.placa ?? "N/A" }}
          </template>
        </b-table-column>

        <b-table-column field="tipo" label="Tipo">
          <template slot-scope="props">
            {{ props.row.tipo }}
          </template>
        </b-table-column>

        <b-table-column field="classificação" label="Classificação">
          <template slot-scope="props">
            {{ formatClassificacao(props.row.veiculo.classificacao) ?? "N/A" }}
          </template>
        </b-table-column>

        <b-table-column field="empresa" label="Empresa">
          <template slot-scope="props">
            {{ props.row.empresa.razaoSocial }}
          </template>
        </b-table-column>

        <b-table-column field="status" label="Status">
          <template slot-scope="props">
            <StatusTag :status="props.row.status" />
          </template>
        </b-table-column>

        <b-table-column field="ações" label="Ações">
          <template slot-scope="props">
            <ActionButtons
              :item="props.row"
              :current-route="currentRoute"
              :toConfig="{
                name: 'EditarAcessoVeiculo',
                params: { id: props.row.id },
              }"
              :toApprove="true"
              :toRefuse="true"
              :viewMotivoRecusa="props.row.motivoRecusa && props.row.status === 'RECUSADO' ? props.row.motivoRecusa : null"
              @viewMotivoRecusa="(motivo) => abrirModalMotivoRecusa(motivo)"
              @delete="(p) => openModalDelete(p)"
              @approve="(id) => aprovarAcesso(id)"
              @refuse="(payload) => openModalRecusar(payload)"
            />
          </template>
        </b-table-column>
      </b-table>
      <!-- Paginação -->
      <pagination-section
        :current-page="pageable.current"
        :total-items="totalElements"
        :per-page="15"
        :current-query="$route.query"
        @page-change="handlePageChange"
      />
    </CardComponent>
  </div>
</template>

<script>
import { defineComponent } from "vue";

import AcessosService from "@/services/AcessosService";

import TitleBar from "@/components/TitleBar.vue";
import CardHeader from "@/components/Cards/CardHeader.vue";
import CardComponent from "@/components/Cards/CardComponent.vue";
import PaginationSection from "@/components/PaginationSection.vue";
import StatusTag from "@/components/StatusTag.vue";
import ActionButtons from "@/components/ActionButtons.vue";
import ModalDelete from "@/components/Modals/ModalDelete.vue";
import ModalRecusarAcesso from "@/components/Modals/ModalRecusarAcesso.vue";
import FilterAcessoVeiculo from "@/components/Filters/FilterAcessoVeiculo.vue";

export default defineComponent({
  name: "ViewListAcessoVeiculo",
  components: {
    TitleBar,
    CardHeader,
    CardComponent,
    PaginationSection,
    StatusTag,
    ActionButtons,
    ModalDelete,
    FilterAcessoVeiculo,
    ModalRecusarAcesso
  },
  data() {
    return {
      modalDelectActive: false,
      modalRecusarActive: false,
      acessoSelecionado: null,
      acessos: [],
      pageable: {
        current: 1,
        size: 0,
      },
      totalElements: 0,
    };
  },
  computed: {
    currentRoute() {
      return this.$route;
    },
  },
  mounted() {
    this.loadAcessos();
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.loadAcessos();
      },
    },
  },

  methods: {
    handlePageChange(newPage) {
      this.pageable.current = newPage;
      this.$router.push({
        query: {
          ...this.$route.query,
          page: newPage,
        },
      });
    },
    handleFilter(filter) {
      const newQuery = {
        ...this.currentRoute.query,
        ...filter,
        page: 1,
      };
      Object.keys(newQuery).forEach((key) => {
        if (!newQuery[key]) delete newQuery[key];
      });

      this.$router.push({ query: newQuery });
    },
    async loadAcessos() {
      try {
        const page = Number(this.$route.query.page) || 0;
        this.pageable.current = page;

        const response = await AcessosService.getAcessosVeiculos(
          page - 1,
          this.$util.formatQueryUrl(this.currentRoute.query)
        );

        this.acessos = response.data.content;
        this.totalElements = response.data.totalElements;
        this.pageable.size = response.data.size; // Correção do tamanho da página
      } catch (error) {
        console.error(error);
      }
    },
    async aprovarAcesso(id) {
      try {
        await AcessosService.setStatusAcessoVeiculo(id, "ATIVO");
        this.$buefy.toast.open({
          message: `Acesso Aprovado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
        this.loadAcessos();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response.data.message,
          position: "is-bottom",
          type: "is-danger",
        });
      }
    },
    async recusarAcesso(payload) {
      try {
        const { id, motivoRecusa } = payload;
        await AcessosService.setRecusarAcessoVeiculo(id, motivoRecusa);
        this.$buefy.toast.open({
          message: `Acesso Recusado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
        this.loadAcessos();
      } catch (error) {
        this.$buefy.toast.open({
          message: error.response.data.message,
          position: "is-bottom",
          type: "is-danger",
        });
      }
      this.modalRecusarActive = false;
      this.acessoSelecionado = null;
    },
    async deleteAcesso(id) {
      try {
        console.log("deleting id:", id);
        await AcessosService.setStatusAcessoVeiculoInativo(id);
        this.loadAcessos();
        this.closeModal();
      } catch (error) {
        console.error(error);
      } finally {
        this.$buefy.toast.open({
          message: `Acesso inativado com sucesso!`,
          position: "is-bottom",
          type: "is-info",
        });
      }
    },
    openModalDelete(item) {
      this.modalDelectActive = true;
      this.acessoSelecionado = item;
    },
    openModalRecusar(item) {
      this.modalRecusarActive = true;
      this.acessoSelecionado = item;
    },
    abrirModalMotivoRecusa(motivo) {
      this.$buefy.dialog.alert({
        title: "Motivo da Recusa",
        message: motivo,
        type: "is-danger",
        hasIcon: true,
        icon: "times-circle",
        iconPack: "fa",
        ariaRole: "alertdialog",
        ariaModal: true,
      });
    },
    closeModal() {
      this.modalDelectActive = false;
      this.modalRecusarActive = false;
      this.acessoSelecionado = null;
    },
    formatClassificacao(classificacao) {
      return classificacao.replace("_", " ");
    },
  },
});
</script>

<style>
.tabela {
  margin-bottom: 8px;
  width: 100%;
  min-height: 500px;
}
</style>
